// eslint-disable-next-line
import { makeAutoObservable, runInAction } from "mobx";
import {GroupedOption, IOption} from "../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import RmaListService, { IReturnReason, PAYMENT_STATUS_TYPE, RESERVED_ITEM_EXPIRATION_TYPE, RESTOCK_VALIDATED_TYPE } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import { IChangeable } from "../../../../stores/types";
import { RMA_QUICK_FILTERS } from "./rma-paged-list-store";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import { IReportFiltersType } from "../../../../services/api/report-service";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import { ReservedItemDaysRemainingStore } from "../components/rma-list-filters-drawer/reserved-item-expiration-dropdown/reserved-item-days-remaining-store";
import ResolutionsPreset from "../../../policies/stores/presets/resolutions-preset";

interface IRmaListFilters {
    search_input: string,
    search_exact_match: boolean,
    filter_resolution_id: number | null,
    filter_return_reason_id: number | null,
    filter_restock_validated_type: RESTOCK_VALIDATED_TYPE,
    filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE,
    filter_date_range: string,
    filter_reserve_item_expiration_input: string,
    filter_status?: string,
    filter_payment_status_type: PAYMENT_STATUS_TYPE,
}
interface IRmaListFiltersChanges extends Partial<IRmaListFilters> { };

export interface IFiltersButtonstyle {
    variant: BUTTON_VARIANTS,
    color: string,
}

class RmaListFiltersStore implements IChangeable<IRmaListFilters> {
    private _isLoaded: boolean = false;
    private _isLoading: boolean = false;
    private _returnReasonOptions?: IReturnReason[] = [];
    private _resolutionsPreset: ResolutionsPreset = {} as ResolutionsPreset;
    private _model: IRmaListFilters;
    private _changes: IRmaListFiltersChanges = {};
    private _scope = 'dashboard-returns-page';
    private _reservedItemDaysRemainingStore: ReservedItemDaysRemainingStore = new ReservedItemDaysRemainingStore();

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
        this._model = this._createModelFromQuery();
    }

    protected get _rmaListService(): RmaListService { return rootContainer.get<RmaListService>(TYPE.RmaListService); }

    public get searchInput() {
        return this._changes.search_input ?? this._model.search_input;
    }

    private get status(): string | undefined {
        return this._changes.filter_status ?? this._model.filter_status;
    }

    public get searchURLParams(): URLSearchParams {
        const search = this.searchInput;
        const status = this.status;
        const newUrlSearchParams = new URLSearchParams();

        if (search) {
            newUrlSearchParams.set('search_input', search);
        }

        if (status) {
            newUrlSearchParams.set('filter_status', status);
        }

        return newUrlSearchParams;
    }

    public get searchExactMatch() {
        if (this._changes.search_exact_match === false) {
            return false;
        }
        return this._changes.search_exact_match ?? this._model.search_exact_match;
    }

    get isLoading() {
        return this._isLoading;
    }

    get selectedResolutionId() {
        if (this._changes.filter_resolution_id === null) {
            return null;
        }
        return this._changes.filter_resolution_id ?? this._model.filter_resolution_id;
    }

    get selectedReturnReasonId() {
        if (this._changes.filter_return_reason_id === null) {
            return null;
        }
        return this._changes.filter_return_reason_id ?? this._model.filter_return_reason_id;
    }

    get restockValidatedType() {
        return this._changes.filter_restock_validated_type ?? this._model.filter_restock_validated_type;
    }

    get reserveItemExpiration() {
        return this._changes.filter_reserve_item_expiration ?? this._model.filter_reserve_item_expiration;
    }

    get reserveItemExpirationInput() {
        return this._changes.filter_reserve_item_expiration_input ?? this._model.filter_reserve_item_expiration_input;
    }

    get reserveItemExpirationFilterValid() {
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE && !this.reserveItemExpirationInput) {
            return false;
        }
        return true;
    }

    get dateRange() {
        return this._changes.filter_date_range ?? this._model.filter_date_range;
    }

    get paymentStatus() {
        return this._changes.filter_payment_status_type ?? this._model.filter_payment_status_type;
    }

    get selectedQuickFilter() {
        return this._changes.filter_status ?? this._model.filter_status;
    }

    private get _allOption() {
        return {
            label: dashboardTranslationsStore.getTranslation('ALL', this._scope),
            value: 0 /** Select dropdown options doesn't accept null */
        }
    }

    get resolutionsOptions(): GroupedOption[] {
        if (!this._resolutionsPreset) {
            return [];
        }
        return [
            {category: '', options: [this._allOption]},
            ...this._resolutionsPreset.getOptionsPreset()
        ];
    }

    get selectedResolutionValue(): IOption | undefined {
        if (!this.selectedResolutionId) {
            return this._allOption;
        }

        const selectedResolution = this._resolutionsPreset.getPresetById(this.selectedResolutionId);
        if (!selectedResolution) {
            return undefined;
        }

        return {
            label: selectedResolution.internal_name,
            value: selectedResolution.id
        };
    }

    get returnReasonsOptions() {
        if (!this._returnReasonOptions) {
            return [];
        }
        return [
            this._allOption,
            ...this._returnReasonOptions?.map(returnReason => (
                {
                    label: returnReason.name,
                    value: returnReason.id
                }
            ))
        ];
    }

    get selectedReturnReasonValue() {
        if (!this.selectedReturnReasonId) {
            return this._allOption;
        }
        return this.returnReasonsOptions?.find(res => res.value === this.selectedReturnReasonId)
    }

    get restockValidatedOptions() {
        return [
            {
                value: RESTOCK_VALIDATED_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.VALIDATED,
                label: dashboardTranslationsStore.getTranslation('VALIDATED', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.PARTIALLY_VALIDATED,
                label: dashboardTranslationsStore.getTranslation('PARTIALLY_VALIDATED', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.NOT_VALIDATED,
                label: dashboardTranslationsStore.getTranslation('NOT_VALIDATED', this._scope)
            }
        ];
    }

    get restockValidatedValue() {
        return this.restockValidatedOptions?.find(res => res.value === this.restockValidatedType)
    }

    get reserverdItemExpirationOptions() {
        return [
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE,
                label: 'By date range'
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING,
                label: 'By days remaining'
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.EXPIRED,
                label: 'Expired'
            },
        ];
    }

    get reserverdItemExpirationValue() {
        return this.reserverdItemExpirationOptions?.find(res => res.value === this.reserveItemExpiration)
    }

    public get reservedItemDaysRemainingStore(): ReservedItemDaysRemainingStore {
        return this._reservedItemDaysRemainingStore;
    }

    get paymentStatusFilterOptions() {
        return [
            {
                value: PAYMENT_STATUS_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.NOT_REFUNDED,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_NOT_REFUNDED', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.REFUNDED,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_REFUNDED', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.NOT_PAID,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_NOT_PAID', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.PAID,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_PAID', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.EXPIRING,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_EXPIRING', this._scope)
            }
        ];
    }

    public setSearchCustomerId(value: string) {
        this._changes.search_input = value;
        this._clearUnneededChanges();
    }

    public setSelectedResolutionId(value: number | null) {
        this._changes.filter_resolution_id = value;
        this._clearUnneededChanges();
    }

    public setSelectedReturnReasonId(value: number | null) {
        this._changes.filter_return_reason_id = value;
        this._clearUnneededChanges();
    }

    public setRestockValidatedType(value: RESTOCK_VALIDATED_TYPE) {
        this._changes.filter_restock_validated_type = value;
        this._clearUnneededChanges();
    }

    public setPaymentStatusType(value: PAYMENT_STATUS_TYPE) {
        this._changes.filter_payment_status_type = value;
        this._clearUnneededChanges();
    }

    public setReserverdItemExpiration(value: RESERVED_ITEM_EXPIRATION_TYPE) {
        this._changes.filter_reserve_item_expiration = value;
        this._clearUnneededChanges();
    }

    public setReserverdItemExpirationInput(value: string | undefined) {
        this._changes.filter_reserve_item_expiration_input = value;
        this._clearUnneededChanges();
    }

    public setDateRange(value: string) {
        this._changes.filter_date_range = value;
        this._clearUnneededChanges();
    }

    public setSelectedQuickFilter(value: RMA_QUICK_FILTERS) {
        this._changes.filter_status = value;
        this._clearUnneededChanges();
    }

    public toggleSearchExactMatch() {
        this._changes.search_exact_match = !this.searchExactMatch;
        this._clearUnneededChanges();
    }

    public async loadFilterOptions() {
        if (this._isLoaded) {
            return;
        }

        try {
            this._isLoading = true;
            const filters = await this._rmaListService.fetchFilters();
            runInAction(() => {
                this._returnReasonOptions = filters.return_reasons;
                this._resolutionsPreset = new ResolutionsPreset(filters.resolutions);
                this._isLoading = false;
                this._isLoaded = true;
            });
        } catch (error) {
            runInAction(() => {
                this._isLoading = false;
            });
            console.log(error);
        }
    }

    private _createModelFromQuery(): IRmaListFilters {
        // Replace + with %2B before parsing to preserve literal + characters in search input
        // URLSearchParams decodes + as space per application/x-www-form-urlencoded standard
        const rawSearch = document.location.search.replace(/\+/g, '%2B');
        const params = new URLSearchParams(rawSearch);

        const filterStatus = params.get('filter_status');
        const searchInput = params.get('search_input');

        return {
            search_input: searchInput ?? '',
            search_exact_match: false,
            filter_resolution_id: null,
            filter_return_reason_id: null,
            filter_restock_validated_type: RESTOCK_VALIDATED_TYPE.ALL,
            filter_date_range: '',
            filter_status: filterStatus || RMA_QUICK_FILTERS.OPEN,
            filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE.ALL,
            filter_reserve_item_expiration_input: '',
            filter_payment_status_type: PAYMENT_STATUS_TYPE.ALL,
        }
    }

    public hasChanges(): boolean {
        const reservedItemDaysRemainingChanges = this._reservedItemDaysRemainingStore.hasChanges();
        return reservedItemDaysRemainingChanges || !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {};
        this._reservedItemDaysRemainingStore.discardChanges();
    }

    public commitChanges(): void {
        this._reservedItemDaysRemainingStore.commitChanges();
        this._model = this.getUpdatedModel();
        this.discardChanges();
    }

    public getUpdatedModel(): IRmaListFilters {
        const updatedModel = { ...this._model, ...this._changes };
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING) {
            const daysRemainingModel = this._reservedItemDaysRemainingStore.getUpdatedModel();
            updatedModel.filter_reserve_item_expiration_input = `${daysRemainingModel.from}-${daysRemainingModel.to}`;
        }

        return updatedModel;
    }

    public getReportFilters(): IReportFiltersType {
        const filters: IReportFiltersType = {
            filter_is_restock_validated: this.restockValidatedType as string,
            filter_status: this.selectedQuickFilter as string,
            search_query: this.searchInput,
            is_exact_match: this.searchExactMatch ? '1' : '0'
        }
        if (this.selectedResolutionId) {
            filters['filter_resolution'] = this.selectedResolutionId.toString();
        }
        if (this.selectedReturnReasonId) {
            filters['filter_return_reason'] = this.selectedReturnReasonId.toString();
        }
        if (this.dateRange) {
            filters['filter_date'] = this.dateRange;
        }
        if (this.reserveItemExpiration) {
            filters['filter_reserve_item_expiration'] = this.reserveItemExpiration;
        }
        if (this.reserveItemExpirationInput) {
            filters['filter_reserve_item_expiration_input'] = this.reserveItemExpirationInput;
        }
        if (this.paymentStatus) {
            filters['filter_payment_status_type'] = this.paymentStatus;
        }
        return filters;
    }

    public hasActiveDrawerFilters(): boolean {
        const filters = this.getUpdatedModel();
        const { search_exact_match, search_input, filter_status, ...drawerFilters } = filters;
        return Object.values(drawerFilters).some(filter => (filter && filter !== RESTOCK_VALIDATED_TYPE.ALL));
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IRmaListFilters;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

}

export default RmaListFiltersStore;
